import Bluebird from 'bluebird';
import { Injectable, inject } from '@angular/core';
import { CustomFields, IoTypeListItem, ReportDefinitionColumnOptions, ReportLayoutColumn } from '@key-telematics/fleet-api-client';
import { TranslateService } from '@ngx-translate/core';
import { KuiOptionListItem } from 'app/key-ui/option-panel/option-panel.component';
import { AppService } from 'app/app.service';
import { AssetGroupingItem, AssetGroupingService, EventsService } from 'app/services';
import { skipWhile, take } from 'rxjs/operators';

// Auto-generated column literals only set a subset of ReportDefinitionColumnOptions
// fields; format/footer are filled in later by toLayoutColumn's definition lookup.
export type TreeColumn = Omit<ReportDefinitionColumnOptions, 'format' | 'footer'> & {
    format?: string;
    footer?: string;
};

export interface ColumnTreeNode {
    id: string;
    name: string;
    children?: ColumnTreeNode[];
    data?: { column: TreeColumn };
}

export interface BuildTreeInput {
    availableColumns: TreeColumn[];
    autoColumns: string[];
}

@Injectable({ providedIn: 'root' })
export class ReportColumnTreeService {

    private app = inject(AppService);
    private grouping = inject(AssetGroupingService);
    private i18n = inject(TranslateService);
    private eventsService = inject(EventsService);

    private ioTypes: IoTypeListItem[] | null = null;

    private ioTypeConfig: { [key: string]: { prefix?: string, types?: string[], type?: string } } = {
        io_start: { prefix: 'ios_', types: ['digital_input', 'digital_output', 'analog_input', 'temperature_input', 'can_input', 'counter_input'] },
        io_end: { prefix: 'ioe_', types: ['digital_input', 'digital_output', 'analog_input', 'temperature_input', 'can_input', 'counter_input'] },
        io_min: { prefix: 'iom_', types: ['analog_input', 'temperature_input', 'can_input'] },
        io_max: { prefix: 'iox_', types: ['analog_input', 'temperature_input', 'can_input'] },
        io_average: { prefix: 'ioa_', types: ['analog_input', 'temperature_input', 'can_input'] },
        io_total: { prefix: 'iot_', types: ['counter_input'] },
        io_rate: { prefix: 'ior_', types: ['counter_input'] },
        digital_input: { prefix: 'io_', type: 'digital_input' },
        digital_output: { prefix: 'io_', type: 'digital_output' },
        analog_input: { prefix: 'io_', type: 'analog_input' },
        temperature_input: { prefix: 'io_', type: 'temperature_input' },
        can_input: { prefix: 'io_', type: 'can_input' },
        counter_input: { prefix: 'io_', type: 'counter_input' },
    };

    // Prefixes the UI formats itself: it derives the same field name the report service derives when
    // the layout supplies none — aggregates get the prefix and a lowercased slug (ios_batterypercentage),
    // bare io columns get the slug alone with its casing intact (BatteryPercentage) — then wraps it in
    // Unit() if the io type carries one. `footer` mirrors the aggregate the report service generates for
    // that prefix. ior_ is absent; the report service owns rate columns end to end.
    private ioColumnFormats: { [prefix: string]: { footer?: (field: string) => string } } = {
        ios_: { footer: f => `First(Fields!${f}, 'Asset')` },
        ioe_: { footer: f => `Last(Fields!${f}, 'Asset')` },
        iom_: { footer: f => `Min(Convert.ToDouble(Fields!${f}), 'Asset')` },
        iox_: { footer: f => `Max(Convert.ToDouble(Fields!${f}), 'Asset')` },
        ioa_: { footer: f => `Avg(Fields!${f}, 'Asset')` },
        iot_: { footer: f => `Sum(Fields!${f}, 'Asset')` },
        io_: {},
    };

    buildTree(input: BuildTreeInput): Bluebird<ColumnTreeNode[]> {
        const { availableColumns, autoColumns } = input;

        const nodes: ColumnTreeNode[] = [];

        const standardNode: ColumnTreeNode = {
            id: 'standard',
            name: this.i18n.instant('REPORTING.BUILDER.COLUMNS.TREE.STANDARD'),
            children: availableColumns.map<ColumnTreeNode>(column => ({
                id: column.id,
                name: column.title,
                data: { column },
            })),
        };
        nodes.push(standardNode);

        return Bluebird.map(autoColumns || [], autoColumn => {
            switch (autoColumn) {
                case 'digital_input':
                case 'digital_output':
                case 'analog_input':
                case 'temperature_input':
                case 'can_input':
                case 'counter_input':
                case 'io_start':
                case 'io_end':
                case 'io_min':
                case 'io_max':
                case 'io_average':
                case 'io_total':
                case 'io_rate':
                    return this.getIoNode(autoColumn, autoColumn.startsWith('io_'));
                case 'event_count':
                    return this.getEventCountNode();
                case 'linked_asset':
                    return this.getLinkedAssetsNode();
                case 'custom_fields_asset':
                case 'custom_fields_device':
                case 'custom_fields_event':
                case 'custom_fields_asset_tag':
                case 'custom_fields_sim_card':
                    return this.getCustomFieldsNode(autoColumn);
                default:
                    return null;
            }
        }, { concurrency: 1 }).then(items => nodes.concat(items.filter(x => x)));
    }

    private getIoTypes(): Promise<IoTypeListItem[]> {
        if (this.ioTypes) {
            return Promise.resolve(this.ioTypes);
        }
        return this.app.api.entities.listIoTypes(this.app.client.id, true, 0, 300, 'name', 'state=active').then(result => {
            this.ioTypes = result.items;
            return this.ioTypes;
        });
    }

    private getIoNode(autoColumn: string, group: boolean): Promise<ColumnTreeNode> {
        const ioTypeToNode = (config: { prefix?: string }, ioType: IoTypeListItem, name: string): ColumnTreeNode => {
            const id = config.prefix + ioType.id;
            const column: TreeColumn = {
                id,
                title: group ? `${name} ${ioType.name}` : ioType.name,
                width: 40,
                align: 'Right',
                def: false,
                required: false,
            };

            const format = this.ioColumnFormats[config.prefix];
            if (format) {
                const slug = ioType.name.replace(/[^a-zA-Z0-9_]/g, '');
                column.field = config.prefix === 'io_' ? slug : (config.prefix + slug).toLowerCase();

                const unit = ioType.unit?.replace(/"/g, '').trim();
                if (unit) {
                    column.format = `=Unit(Fields!${column.field}.Value, 2, "${unit}")`;
                    if (format.footer) {
                        column.footer = `=Unit(${format.footer(column.field)}, 2, "${unit}")`;
                    }
                }
            }

            return { id, name: ioType.name, data: { column } };
        };

        const config = this.ioTypeConfig[autoColumn];
        if (!config) {
            return Promise.resolve(null);
        }
        const name = this.i18n.instant(`REPORTING.BUILDER.COLUMNS.TREE.${autoColumn.toUpperCase()}`);
        return this.getIoTypes().then(ioTypes => {
            const allIoTypes = ioTypes.filter(x => group ? config.types.indexOf(x.type) !== -1 : config.type === x.type);
            const vendorIoTypes = allIoTypes.filter(f => f.owner?.type === 'vendor');
            const clientIoTypes = allIoTypes.filter(f => f.owner?.type === 'client');
            return {
                id: autoColumn,
                name,
                children: [
                    vendorIoTypes?.length > 0 && {
                        id: config.prefix + 'vendor',
                        name: 'Vendor',
                        children: vendorIoTypes.map(ioType => ioTypeToNode(config, ioType, name)),
                    },
                    clientIoTypes?.length > 0 && {
                        id: config.prefix + 'client',
                        name: 'Client',
                        children: clientIoTypes.map(ioType => ioTypeToNode(config, ioType, name)),
                    },
                ].filter(x => x),
            };
        });
    }

    private getEventCountNode(): Promise<ColumnTreeNode> {
        const eventTypes = this.eventsService.getEventTypes();
        return Promise.resolve({
            id: 'event_count',
            name: this.i18n.instant('REPORTING.BUILDER.COLUMNS.TREE.EVENT_COUNT'),
            children: eventTypes.map<ColumnTreeNode>(eventType => ({
                id: eventType.id,
                name: eventType.name,
                data: {
                    column: {
                        id: `evt_cnt_${eventType.id}`,
                        title: this.i18n.instant('REPORTING.BUILDER.COLUMNS.TREE.EVENT_COUNT_TITLE', { eventName: eventType.name }),
                        width: 30,
                        align: 'Center',
                        def: false,
                        required: false,
                    },
                },
            })),
        });
    }

    private getLinkedAssetsNode(): Promise<ColumnTreeNode> {
        return this.grouping.getAssetTypes().then(assetTypes => ({
            id: 'linked',
            name: this.i18n.instant('REPORTING.BUILDER.COLUMNS.TREE.LINKED'),
            children: assetTypes.map<ColumnTreeNode>(assetType => {
                const assetTypeName = this.i18n.instant(`SHARED.ASSET_TYPES.${assetType.data.name.toUpperCase().replace(/ /g, '_')}`);
                return {
                    id: assetType.id,
                    name: assetTypeName,
                    data: {
                        column: {
                            id: `linked_asset_${assetType.id}`,
                            title: assetTypeName,
                            width: 100,
                            align: 'Left',
                            def: false,
                            required: false,
                        },
                    },
                };
            }),
        }));
    }

    private isCustomFieldValid(autoColumn: string, key: string, assetTypes: AssetGroupingItem[]): boolean {
        return autoColumn === 'custom_fields_asset' && (key === 'asset' || assetTypes.map(at => at.id).indexOf(key) !== -1)
            || autoColumn === 'custom_fields_event' && key === 'event'
            || autoColumn === 'custom_fields_device' && key === 'device'
            || autoColumn === 'custom_fields_sim_card' && key === 'simcard'
            || autoColumn === 'custom_fields_asset_tag' && key === 'assettag';
    }

    private getCustomFieldsNode(autoColumn: string): Promise<ColumnTreeNode> {
        const mapFields = (fields: CustomFields, key: string, label?: string) => {
            label = label ? ` (${label})` : '';
            return (fields[key] || []).map(field => ({
                id: field.id,
                title: field.title,
                name: field.title + label,
                type: key,
                owner: field.owner,
            }));
        };

        const getFieldsFromCompany = (type: 'client' | 'vendor', customFields: CustomFields, column: string, assetTypes: AssetGroupingItem[]) => {
            if (!customFields) return [];
            return Object.keys(customFields).filter(x => this.isCustomFieldValid(column, x, assetTypes)).map(key => {
                const assetType = assetTypes.find(x => x.id === key);
                if (assetType) {
                    // Need to use assetType.data.name as assetType.name may already be translated.
                    const assetTypeLabel = this.i18n.instant(`SHARED.ASSET_TYPES.${assetType.data.name.toUpperCase().replace(/ /g, '_')}`);
                    return mapFields(customFields, key, assetTypeLabel);
                } else if (key === 'asset') {
                    return mapFields(customFields, key, this.i18n.instant(`SHARED.ASSET_TYPES.ASSET`));
                }
                return mapFields(customFields, key);
            }).reduce((p, c) => p.concat(c), []).filter(f => f.owner === type);
        };

        const fieldToNode = (field: { id: string, title: string, name: string, type: string, owner: string }): ColumnTreeNode => {
            let type: string;
            switch (field.type) {
                case 'event': type = `event_custom_field_${field.id}`; break;
                case 'device': type = `device_custom_field_${field.id}`; break;
                case 'assettag': type = `asset_tag_custom_field_${field.id}`; break;
                case 'simcard': type = `sim_card_custom_field_${field.id}`; break;
                default: type = `custom_field_${field.id}`;
            }
            return {
                id: field.id,
                name: field.name,
                data: {
                    column: {
                        id: type,
                        title: field.title,
                        width: 80,
                        align: 'Left',
                        def: false,
                        required: false,
                    },
                },
            };
        };

        return this.app.client$.pipe(skipWhile(client => !client), take(1)).toPromise().then(async client => {
            const assetTypes = await this.grouping.getAssetTypes();
            const clientFields = getFieldsFromCompany('client', client.customFields, autoColumn, assetTypes);
            const vendorFields = getFieldsFromCompany('vendor', client.customFields, autoColumn, assetTypes);
            const fields = [...clientFields, ...vendorFields];
            if (fields.length === 0) return null;
            return {
                id: 'custom_fields',
                name: this.i18n.instant('REPORTING.BUILDER.COLUMNS.TREE.CUSTOM_FIELDS'),
                children: [
                    vendorFields?.length > 0 && {
                        id: 'vendor',
                        name: this.i18n.instant('ADMIN.ENTITIES.VENDOR'),
                        children: vendorFields.map(fieldToNode),
                    },
                    clientFields?.length > 0 && {
                        id: 'client',
                        name: this.i18n.instant('ADMIN.ENTITIES.CLIENT'),
                        children: clientFields.map(fieldToNode),
                    },
                ].filter(x => x),
            };
        });
    }
}

export function toReportLayoutColumn(column: TreeColumn): ReportLayoutColumn {
    return {
        id: column.id,
        title: column.title,
        width: column.width,
        align: column.align,
        field: column.field,
        format: column.format,
        footer: column.footer,
        footerFormat: column.footerFormat,
        headerFormat: column.headerFormat,
        chartFormat: column.chartFormat,
        nowrap: column.nowrap,
        def: column.def,
        req: column.required,
    };
}

export function fromReportLayoutColumn(column: ReportLayoutColumn): TreeColumn {
    return {
        id: column.id,
        title: column.title,
        width: column.width,
        align: column.align,
        field: column.field,
        format: column.format,
        footer: column.footer,
        footerFormat: column.footerFormat,
        headerFormat: column.headerFormat,
        chartFormat: column.chartFormat,
        nowrap: column.nowrap,
        def: column.def,
        required: column.req,
    };
}

export function toOptionListItems(
    nodes: ColumnTreeNode[],
    disabled: boolean,
    onLeaf: (column: ReportLayoutColumn) => unknown,
): KuiOptionListItem[] {
    return nodes.map(node => {
        if (node.children && node.children.length > 0) {
            return {
                id: node.id,
                name: node.name,
                disabled,
                children: toOptionListItems(node.children, disabled, onLeaf),
            };
        }
        if (node.data?.column) {
            return {
                id: node.id,
                name: node.name,
                disabled,
                icon: 'icon icon-table-columns',
                value: !disabled && onLeaf(toReportLayoutColumn(node.data.column)),
            };
        }
        return { id: node.id, name: node.name };
    });
}
